import { useEffect, useRef, useState } from 'react';
import { useOutsideClickEffect } from '@toss/react';
import { clamp } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { MinusIcon, PlusIcon } from '@zep/icons';
import { cn } from '@zep/utils';
type MaxCcuQuantityInputProps = {
  min: number;
  max?: number;
  initialValue: number;
  onChange?: (value: number) => void;
};

/**
 * MaxCcuQuantityInput 컴포넌트는 사용자가 숫자 값을 입력하거나 증가/감소 버튼을 통해 값을 조정할 수 있는 입력 필드입니다.
 *
 * 주요 요구사항:
 * - 사용자는 10의 배수가 아닌 값을 입력할 수 있지만, 입력 필드가 포커스를 잃을 때 해당 값은 자동으로 올림 처리되어 10의 배수로 변경됩니다.
 * - 증가(+) 버튼을 누르면 값이 10씩 증가하고, 감소(-) 버튼을 누르면 값이 10씩 감소합니다.
 * - 값은 `min`과 `max` 범위 내에 있어야 하며, 이 범위를 벗어나지 않도록 강제됩니다.
 * - 입력 중 빈 값이 되면 빈 문자열로 처리되며, 0으로 시작하는 숫자는 자동으로 제거됩니다.
 * @param {number} [min] - 입력 가능한 최소 값.
 * @param {number} [max] - 입력 가능한 최대 값.
 * @param {number} [initialValue] - 초기 입력 값.
 * @param onChange
 */
export const MaxCcuQuantityInput = ({
  initialValue = 20,
  min = initialValue,
  max = 1000,
  onChange
}: MaxCcuQuantityInputProps) => {
  const {
    t
  } = useTranslation(['subscription']);
  const [value, setValue] = useState<string | number>(initialValue);
  const buttonPressed = useRef(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleIncrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const newValue = clamp(Number(value) + 10, min, max);
    setValue(newValue);
    onChange?.(newValue);
  };
  const handleDecrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const newValue = clamp(Number(value) - 10, min, max);
    setValue(newValue);
    onChange?.(newValue);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue); // 자유롭게 입력을 받습니다.
  };
  const handleBlur = () => {
    if (!buttonPressed.current) {
      let newValue = isEmptyString(value) ? min : Number(value);
      newValue = ceilToNearestTen(clamp(newValue, min, max));
      setValue(newValue);
      onChange?.(newValue);
    }
  };
  useOutsideClickEffect(ref.current, handleBlur);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  return <div ref={ref} className="flex items-center gap-[10px]" data-sentry-component="MaxCcuQuantityInput" data-sentry-source-file="MaxCcuQuantityInput.tsx">
      <div className="flex items-center overflow-hidden rounded-[8px] border border-gray-300">
        <button className={cn('flex size-[36px] items-center justify-center bg-gray-200 px-4 py-2 font-semibold text-gray-900 hover:bg-gray-300 focus:outline-none', 'disabled:cursor-default disabled:opacity-30 disabled:hover:bg-gray-200')} onClick={handleDecrement} disabled={typeof value === 'number' && value <= min}>
          <span className="size-[20px]">
            <MinusIcon width={20} height={20} className="text-gray-900/[0.64]" data-sentry-element="MinusIcon" data-sentry-source-file="MaxCcuQuantityInput.tsx" />
          </span>
        </button>
        <input ref={inputRef} type="number" className="h-[36px] w-[88px] appearance-none text-center text-lg font-semibold text-gray-900 focus:border focus:border-primary focus:outline-none" value={value} onChange={handleInputChange} onKeyDown={e => {
        if (e.key === 'Enter') {
          handleBlur();
        }
      }} />
        <button className="flex size-[36px] items-center justify-center bg-gray-200 px-4 py-2 font-semibold text-gray-900 hover:bg-gray-300 focus:outline-none disabled:cursor-default disabled:opacity-30" onClick={handleIncrement} disabled={typeof value === 'number' && value >= max}>
          <span className="size-[20px]">
            <PlusIcon width={20} height={20} className="text-gray-900/[0.64]" data-sentry-element="PlusIcon" data-sentry-source-file="MaxCcuQuantityInput.tsx" />
          </span>
        </button>
      </div>
      <span className="text-body-1-sb font-[600] text-gray-900">
        {t('subscription:payment.planSelect.people', '명')}
      </span>
    </div>;
};

// TODO: 별도 utils 로 분리해야함. 현재는 libs 하위 common의 util들에 존재 여부를 파악하기 쉽지 않아 추후에 일원화할 예정
const isEmptyString = (val: string | number): boolean => val === '';
const ceilToNearestTen = (value: number): number => {
  return Math.ceil(value / 10) * 10;
};